import { useCallback, useEffect, useRef, useState } from "react";

export interface StopwatchApi {
  /** Elapsed time in milliseconds. */
  elapsed: number;
  running: boolean;
  /** Lap timestamps (cumulative elapsed ms at each lap). */
  laps: number[];
  start: () => void;
  pause: () => void;
  reset: () => void;
  lap: () => void;
}

/**
 * Stopwatch counting up from zero. Elapsed time is computed from timestamps
 * (start instant + accumulated base), so pausing/resuming stays accurate.
 */
export function useStopwatch(): StopwatchApi {
  const [elapsed, setElapsed] = useState(0);
  const [running, setRunning] = useState(false);
  const [laps, setLaps] = useState<number[]>([]);
  const startRef = useRef<number | null>(null);
  const baseRef = useRef(0);

  const currentElapsed = () =>
    baseRef.current +
    (startRef.current != null ? Date.now() - startRef.current : 0);

  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setElapsed(currentElapsed()), 50);
    return () => clearInterval(id);
  }, [running]);

  const start = useCallback(() => {
    startRef.current = Date.now();
    setRunning(true);
  }, []);

  const pause = useCallback(() => {
    baseRef.current = currentElapsed();
    startRef.current = null;
    setElapsed(baseRef.current);
    setRunning(false);
  }, []);

  const reset = useCallback(() => {
    startRef.current = null;
    baseRef.current = 0;
    setElapsed(0);
    setLaps([]);
    setRunning(false);
  }, []);

  const lap = useCallback(() => {
    setLaps((prev) => [...prev, currentElapsed()]);
  }, []);

  return { elapsed, running, laps, start, pause, reset, lap };
}
